import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Routes } from '../routes';

interface CreditActionsPanelProps {
    plan: string;
    creditsAvailable: number;
    companyHasStripeCustomer?: boolean;
    /** Masque factures / moyens de paiement Stripe pour les entreprises SSO Microsoft */
    useMicrosoftMarketplace?: boolean;
    onManageSubscription?: () => void;
    onTopUp?: () => void;
    onViewHistory?: () => void;
    onPlanHistory?: () => void;
    onViewInvoices?: () => void;
    onViewPaymentMethods?: () => void;
    loading?: boolean;
}

const CreditActionsPanel: React.FC<CreditActionsPanelProps> = ({
    plan,
    creditsAvailable,
    companyHasStripeCustomer = false,
    useMicrosoftMarketplace = false,
    onManageSubscription,
    onTopUp,
    onViewHistory,
    onPlanHistory,
    onViewInvoices,
    onViewPaymentMethods,
    loading = false
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    return (
        <div className="d-flex flex-column gap-2">
            {/* Bouton Gérer subscription (PLUS uniquement) — Stripe ou Azure Marketplace */}
            {plan === 'PLUS' && onManageSubscription && (
                <button 
                    className="btn btn-outline-secondary btn-sm w-100"
                    onClick={onManageSubscription}
                    disabled={loading}
                >
                    <i className={`fa-solid ${useMicrosoftMarketplace ? 'fa-store' : 'fa-gear'} me-2`}></i>
                    {useMicrosoftMarketplace
                        ? t('dashboard.manageSubscriptionMicrosoft', 'Gérer via Azure Marketplace')
                        : t('dashboard.manageSubscription', 'Manage Subscription')}
                </button>
            )}

            {/* Bouton Acheter plus de crédits / Voir les plans */}
            {creditsAvailable === 0 ? (
                <button 
                    className="btn btn-warning btn-sm w-100"
                    onClick={() => navigate(Routes.PRICING)}
                >
                    <i className="fa-solid fa-credit-card me-2"></i>
                    {t('dashboard.viewPaidPlans', 'Voir les Plans Payants')}
                </button>
            ) : (
                <>
                    {plan === "FREE" ? (
                        <button 
                            className="btn btn-primary btn-sm w-100"
                            onClick={() => navigate(Routes.PRICING)}
                        >
                            <i className="fa-solid fa-circle-plus me-2"></i>
                            {t('dashboard.viewPaidPlans', 'Voir les Plans Payants')}
                        </button>
                    ) : (
                        <button 
                            className="btn btn-primary btn-sm w-100"
                            onClick={() => {
                                if (onTopUp) {
                                    onTopUp();
                                } else {
                                    navigate(Routes.PRICING);
                                }
                            }}
                            disabled={loading}
                        >
                            <i className="fa-solid fa-circle-plus me-2"></i>
                            {t('dashboard.buyMoreCredits', 'Buy More Credits')}
                        </button>
                    )}
                </>
            )}

            {/* Bouton Historique crédits - COMMENTÉ */}
            {/* {onViewHistory && (
                <button 
                    className="btn btn-info btn-sm w-100"
                    onClick={onViewHistory}
                >
                    <i className="fa-solid fa-chart-line me-2"></i>
                    {t('dashboard.viewCreditHistory', 'Voir l\'historique')}
                </button>
            )} */}

            {/* Bouton Changer de plan */}
            <button 
                className="btn btn-outline-primary btn-sm w-100"
                onClick={() => navigate(Routes.PRICING)}
                disabled={loading}
            >
                <i className="fa-solid fa-right-left me-2"></i>
                {t('dashboard.changePlan', 'Changer de plan')}
            </button>

            {/* Bouton Historique des plans - COMMENTÉ */}
            {/* {onPlanHistory && (
                <button 
                    className="btn btn-outline-secondary btn-sm w-100"
                    onClick={onPlanHistory}
                    disabled={loading}
                >
                    <i className="fa-solid fa-history me-2"></i>
                    {t('dashboard.planHistory', 'Historique des plans')}
                </button>
            )} */}

            {/* Bouton Mes factures (ESSENTIAL ou PLUS, Stripe uniquement) */}
            {!useMicrosoftMarketplace && (plan === 'ESSENTIAL' || plan === 'PLUS') && onViewInvoices && (
                <button 
                    className="btn btn-outline-info btn-sm w-100"
                    onClick={onViewInvoices}
                    disabled={loading}
                >
                    <i className="fa-solid fa-file-invoice me-2"></i>
                    {t('dashboard.myInvoices', 'Mes factures')}
                </button>
            )}

            {/* Bouton Méthodes de paiement (ESSENTIAL ou PLUS, Stripe uniquement) */}
            {!useMicrosoftMarketplace && (plan === 'ESSENTIAL' || plan === 'PLUS') && onViewPaymentMethods && (
                <button 
                    className="btn btn-outline-success btn-sm w-100"
                    onClick={onViewPaymentMethods}
                    disabled={loading}
                >
                    <i className="fa-solid fa-credit-card me-2"></i>
                    {t('dashboard.paymentMethods', 'Moyens de paiement')}
                </button>
            )}

            {/* Bouton Gérer mon plan - Redirige vers pricing */}
            {companyHasStripeCustomer && (
                <button 
                    className="btn btn-warning btn-sm w-100"
                    onClick={() => navigate(Routes.PRICING)}
                    disabled={loading}
                >
                    <i className="fa-solid fa-wrench me-2"></i>
                    {t('dashboard.managePlan', 'Manage Plan')}
                </button>
            )}
        </div>
    );
};

export default CreditActionsPanel;
